import { db, storage } from '../lib/firebase';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  getDocs,
  getDoc,
  serverTimestamp,
  writeBatch,
  limit as firestoreLimit,
  startAfter
} from 'firebase/firestore';

// Collection names
const CHATS_COLLECTION = 'creativeChats';
const FOLDERS_COLLECTION = 'chatFolders';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';

// Firestore has a 1MB document limit. Use 800KB threshold to leave room for overhead
const MAX_DOCUMENT_SIZE_BYTES = 800 * 1024;
// Number of messages per chunk when storing in subcollection
const MESSAGES_PER_CHUNK = 25;

/**
 * Save a creative chat to Firebase
 * @param {Object} chatData - The creative chat data
 * @param {string} userId - The user ID
 * @returns {Promise<string>} - The chat document ID
 */
export async function saveCreativeChat(chatData, userId) {
  try {
    const { id, ...dataToSave } = chatData; // Remove id if it exists

    const chatDoc = {
      ...dataToSave,
      userId,
      updatedAt: serverTimestamp(),
      createdAt: chatData.createdAt || serverTimestamp()
    };

    if (id) {
      // Update existing chat
      const chatRef = doc(db, 'creativeChats', id);
      await updateDoc(chatRef, {
        ...dataToSave,
        updatedAt: serverTimestamp()
      });
      return id;
    } else {
      // Create new chat
      const docRef = await addDoc(collection(db, 'creativeChats'), chatDoc);
      return docRef.id;
    }
  } catch (error) {
    console.error('Error saving creative chat:', error);
    throw error;
  }
}

/**
 * Load all creative chats for a user, optionally filtered by brand
 * @param {string} userId - The user ID
 * @param {string} [brandId] - Optional brand ID to filter by
 * @returns {Promise<Array>} - Array of creative chats
 */
export async function loadCreativeChats(userId, brandId = null) {
  try {
    const chatsRef = collection(db, 'creativeChats');

    // Build query with optional brand filter
    let q;
    if (brandId) {
      q = query(
        chatsRef,
        where('userId', '==', userId),
        where('brandId', '==', brandId),
        orderBy('updatedAt', 'desc')
      );
    } else {
      q = query(
        chatsRef,
        where('userId', '==', userId),
        orderBy('updatedAt', 'desc')
      );
    }

    const querySnapshot = await getDocs(q);
    const chats = [];

    querySnapshot.forEach((doc) => {
      chats.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return chats;
  } catch (error) {
    console.error('Error loading creative chats:', error);
    throw error;
  }
}

/**
 * Load a specific creative chat
 * @param {string} chatId - The chat document ID
 * @returns {Promise<Object>} - The chat data
 */
export async function loadCreativeChat(chatId) {
  try {
    const chatRef = doc(db, 'creativeChats', chatId);
    const chatDoc = await getDoc(chatRef);

    if (!chatDoc.exists()) {
      throw new Error('Chat not found');
    }

    const data = chatDoc.data();

    // Debug: Log product assets being loaded
    console.log('[loadCreativeChat] Loading chat:', chatId);
    console.log('[loadCreativeChat] creativeBrief.selectedAssets:', data.creativeBrief?.selectedAssets?.length || 0, data.creativeBrief?.selectedAssets);
    console.log('[loadCreativeChat] creativeBrief.crawledImages:', data.creativeBrief?.crawledImages?.length || 0, data.creativeBrief?.crawledImages);

    return {
      id: chatDoc.id,
      ...data
    };
  } catch (error) {
    console.error('Error loading creative chat:', error);
    throw error;
  }
}

/**
 * Upload generated image to Firebase Storage and return URL
 * @param {string} imageDataUrl - Base64 data URL of the image
 * @param {string} chatId - The chat ID
 * @param {string} imageId - Unique identifier for the image
 * @returns {Promise<string>} - The download URL
 */
export async function uploadGeneratedImage(imageDataUrl, chatId, imageId) {
  try {
    // Determine file extension from data URL
    const mimeMatch = imageDataUrl.match(/data:image\/([^;]+);/);
    const extension = mimeMatch ? mimeMatch[1].replace('jpeg', 'jpg') : 'png';

    // Use separate folder for creative chat images
    const storageRef = ref(storage, `creative-chat-images/${chatId}/${imageId}.${extension}`);
    await uploadString(storageRef, imageDataUrl, 'data_url');
    const downloadURL = await getDownloadURL(storageRef);
    return downloadURL;
  } catch (error) {
    console.error('Error uploading generated image:', error);
    throw error;
  }
}

/**
 * Upload generated video to Firebase Storage and return URL
 * @param {string} videoDataUrl - Base64 data URL of the video
 * @param {string} chatId - The chat ID
 * @param {string} videoId - Unique identifier for the video
 * @returns {Promise<string>} - The download URL
 */
export async function uploadGeneratedVideo(videoDataUrl, chatId, videoId) {
  try {
    // Determine file extension from data URL
    const mimeMatch = videoDataUrl.match(/data:video\/([^;]+);/);
    const extension = mimeMatch ? mimeMatch[1] : 'mp4';

    // Use separate folder for creative chat videos
    const storageRef = ref(storage, `creative-chat-videos/${chatId}/${videoId}.${extension}`);
    await uploadString(storageRef, videoDataUrl, 'data_url');
    const downloadURL = await getDownloadURL(storageRef);
    return downloadURL;
  } catch (error) {
    console.error('Error uploading generated video:', error);
    throw error;
  }
}

/**
 * Check if a string is a base64 data URL (image or video)
 * @param {string} str - String to check
 * @returns {boolean}
 */
function isDataURL(str) {
  return typeof str === 'string' && (str.startsWith('data:image/') || str.startsWith('data:video/'));
}

/**
 * Check if a string is an image data URL
 * @param {string} str - String to check
 * @returns {boolean}
 */
function isImageDataURL(str) {
  return typeof str === 'string' && str.startsWith('data:image/');
}

/**
 * Check if a string is a video data URL
 * @param {string} str - String to check
 * @returns {boolean}
 */
function isVideoDataURL(str) {
  return typeof str === 'string' && str.startsWith('data:video/');
}

/**
 * Recursively scan an object and upload any base64 data URLs to Storage
 * @param {any} obj - Object to scan
 * @param {string} chatId - The chat ID
 * @param {string} path - Current path in the object (for logging)
 * @returns {Promise<any>} - Object with base64 URLs replaced by Storage URLs
 */
async function uploadBase64InObject(obj, chatId, path = '') {
  if (obj === null || obj === undefined) return obj;

  // If it's a base64 data URL string, upload it (image or video)
  if (typeof obj === 'string' && isDataURL(obj)) {
    try {
      const mediaId = `${path.replace(/\./g, '-')}-${Date.now()}`;

      // Determine if it's an image or video and upload accordingly
      if (isImageDataURL(obj)) {
        const storageUrl = await uploadGeneratedImage(obj, chatId, mediaId);
        console.log(`Uploaded base64 image at ${path}:`, mediaId);
        return storageUrl;
      } else if (isVideoDataURL(obj)) {
        const storageUrl = await uploadGeneratedVideo(obj, chatId, mediaId);
        console.log(`Uploaded base64 video at ${path}:`, mediaId);
        return storageUrl;
      }
    } catch (error) {
      console.error(`Failed to upload base64 at ${path}:`, error);
      return null; // Return null instead of base64 to avoid size issues
    }
  }

  // If it's an array, process each item
  if (Array.isArray(obj)) {
    return Promise.all(obj.map((item, index) =>
      uploadBase64InObject(item, chatId, `${path}[${index}]`)
    ));
  }

  // If it's an object, process each property
  if (typeof obj === 'object') {
    const result = {};
    for (const [key, value] of Object.entries(obj)) {
      result[key] = await uploadBase64InObject(value, chatId, path ? `${path}.${key}` : key);
    }
    return result;
  }

  // Primitive values pass through
  return obj;
}

/**
 * Strip options down to essential fields for database storage
 * Removes imageUrl, imagePrompt, description and other large fields
 * @param {Array} options - Array of options to minimize
 * @returns {Array} - Minimized options with only essential fields
 */
function minimizeOptions(options) {
  if (!options || !Array.isArray(options)) return null;

  return options.map(opt => {
    if (!opt) return opt;
    // Only keep essential fields, strip out image data and long descriptions
    return {
      text: opt.text,
      value: opt.value,
      action: opt.action, // Preserve action for button handlers
      briefKey: opt.briefKey,
      title: opt.title,
      id: opt.id,
      // Explicitly exclude: imageUrl, imagePrompt, description, base64, etc.
    };
  });
}

/**
 * Create a minimal version of creativeBrief for state snapshots
 * Strips out large fields like imageUrls, imagePrompts, descriptions to reduce DB size
 * @param {Object} brief - Full creative brief
 * @returns {Object} - Minimal brief with only essential fields for rewind
 */
function createMinimalBriefForSnapshot(brief) {
  if (!brief) return {};

  // Extract only essential scalar values, not full objects with images
  const minimal = {
    product: brief.product,
    targetAudience: brief.targetAudience,
    desiredEmotion: brief.desiredEmotion,
    objective: brief.objective,
    selectedHeadline: brief.selectedHeadline,
    cta: brief.cta,
    outputType: brief.outputType,
    platform: brief.platform,
    visualStyle: brief.visualStyle,
    tone: brief.tone,
    sceneType: brief.sceneType,
    // Store only IDs/names for selected items, not full objects with images
    selectedCastingId: brief.selectedCasting?.id || null,
    selectedLocationId: brief.selectedLocation?.id || null,
    // Preserve product images - these are URLs (not base64) and needed for video frame generation
    selectedAssets: brief.selectedAssets || null,
    crawledImages: brief.crawledImages || null
  };

  // For videoSettings, only store essential values not image data
  if (brief.videoSettings) {
    minimal.videoSettings = {
      videoLength: brief.videoSettings.videoLength,
      videoStyle: brief.videoSettings.videoStyle,
      dialogueType: brief.videoSettings.dialogueType,
      musicStyle: brief.videoSettings.musicStyle,
      actors: brief.videoSettings.actors,
      scenery: brief.videoSettings.scenery,
      // Store only IDs for frame selections
      firstFrameId: brief.videoSettings.firstFrame?.id || null,
      lastFrameId: brief.videoSettings.lastFrame?.id || null,
      selectedCastingId: brief.videoSettings.selectedCasting?.id || null,
      selectedLocationId: brief.videoSettings.selectedLocation?.id || null,
      // Don't store approvedScript in snapshots - it's too large
      hasApprovedScript: !!brief.videoSettings.approvedScript
    };
  }

  return minimal;
}

/**
 * Prepare chat data for saving
 * @param {Object} chatState - Current chat state
 * @param {string} chatId - The chat ID (required for uploading images)
 * @returns {Promise<Object>} - Serializable chat data
 */
export async function prepareChatDataForSave(chatState, chatId) {
  const {
    messages = [],
    creativeBrief = {},
    briefSheetData = {},
    generatedImages = [],
    creativeMetadata = {},
    currentPhase = 'discovery',
    brandName = '',
    brandId = null,
    experienceLevel = 'guided',
    pendingVideoGeneration = null // Pending video generation for resume on refresh
  } = chatState;

  // Debug: Log product assets before save
  console.log('[prepareChatDataForSave] Input creativeBrief.selectedAssets:', creativeBrief.selectedAssets?.length || 0, creativeBrief.selectedAssets);
  console.log('[prepareChatDataForSave] Input creativeBrief.crawledImages:', creativeBrief.crawledImages?.length || 0, creativeBrief.crawledImages);

  // Generate a temporary chat ID if not provided (for new chats)
  const tempChatId = chatId || `temp-${Date.now()}`;

  // Upload any base64 images in creativeBrief, briefSheetData, and creativeMetadata
  let cleanedCreativeBrief = await uploadBase64InObject(creativeBrief, tempChatId, 'creativeBrief');
  const cleanedBriefSheetData = await uploadBase64InObject(briefSheetData, tempChatId, 'briefSheetData');
  const cleanedCreativeMetadata = await uploadBase64InObject(creativeMetadata, tempChatId, 'creativeMetadata');

  // Strip large text fields from creativeBrief that aren't needed for reload
  // The approvedScript can be very long and is only needed during video generation
  if (cleanedCreativeBrief?.videoSettings?.approvedScript) {
    cleanedCreativeBrief = {
      ...cleanedCreativeBrief,
      videoSettings: {
        ...cleanedCreativeBrief.videoSettings,
        // Store only first 500 chars as preview, or just a flag
        approvedScriptPreview: cleanedCreativeBrief.videoSettings.approvedScript.substring(0, 500) + '...',
        approvedScript: undefined // Remove the full script
      }
    };
  }

  // Also strip imagePrompt fields which can be very long
  const stripImagePrompts = (obj) => {
    if (!obj || typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj.map(stripImagePrompts);

    const cleaned = {};
    for (const [key, value] of Object.entries(obj)) {
      // Skip imagePrompt fields - they can be 1000+ chars each
      if (key === 'imagePrompt') continue;
      cleaned[key] = stripImagePrompts(value);
    }
    return cleaned;
  };

  cleanedCreativeBrief = stripImagePrompts(cleanedCreativeBrief);

  // Debug: Log product assets after processing
  console.log('[prepareChatDataForSave] After processing - selectedAssets:', cleanedCreativeBrief.selectedAssets?.length || 0, cleanedCreativeBrief.selectedAssets);
  console.log('[prepareChatDataForSave] After processing - crawledImages:', cleanedCreativeBrief.crawledImages?.length || 0, cleanedCreativeBrief.crawledImages);

  // Process messages - minimize options and strip large data to reduce DB size
  // Note: We no longer upload base64 in stateSnapshot since we use minimal snapshots
  const cleanedMessages = await Promise.all(
    messages.map(async (msg, index) => {
      let cleanedMsg = { ...msg };

      // Clean msg.data if present - upload any base64 images
      if (msg.data) {
        // Strip videoProgress (transient polling state) but KEEP generatedVideo (completed videos)
        const { videoProgress, ...essentialData } = msg.data || {};
        if (Object.keys(essentialData).length > 0) {
          cleanedMsg.data = await uploadBase64InObject(essentialData, tempChatId, `messages[${index}].data`);
        } else {
          cleanedMsg.data = null;
        }
      }

      // Minimize options - strip imageUrl, imagePrompt, description to reduce size
      // We don't need full image URLs in historical messages
      if (msg.options && Array.isArray(msg.options)) {
        cleanedMsg.options = minimizeOptions(msg.options);
      }

      // stateSnapshot will be minimized when creating finalData
      // No need to process it here

      return cleanedMsg;
    })
  );

  // Upload any base64 images to Storage and get URLs
  const uploadedImages = await Promise.all(
    generatedImages.map(async (img) => {
      // If the image URL is a base64 data URL, upload it to Storage
      if (isDataURL(img.url)) {
        try {
          const storageUrl = await uploadGeneratedImage(img.url, tempChatId, img.id);
          return {
            id: img.id,
            url: storageUrl,
            isPlaceholder: img.isPlaceholder,
            prompt: img.prompt || null
          };
        } catch (error) {
          console.error(`Failed to upload image ${img.id}:`, error);
          // Skip this image if upload fails
          return null;
        }
      } else {
        // Image already has a URL (already uploaded or placeholder)
        return {
          id: img.id,
          url: img.url,
          isPlaceholder: img.isPlaceholder,
          prompt: img.prompt || null
        };
      }
    })
  );

  // Filter out any failed uploads
  const validImages = uploadedImages.filter(img => img !== null);

  // Generate a title from the creative brief
  const title = cleanedCreativeBrief.product
    ? `${cleanedCreativeBrief.product} - ${cleanedCreativeBrief.objective || 'Creative'}`
    : 'Untitled Creative';

  // Create thumbnail from first generated image or placeholder
  let thumbnail = null;
  if (validImages.length > 0 && !validImages[0].isPlaceholder) {
    const imageUrl = validImages[0].url;
    // Ensure thumbnail is an absolute URL (starts with http:// or https://)
    if (imageUrl && (imageUrl.startsWith('http://') || imageUrl.startsWith('https://'))) {
      thumbnail = imageUrl;
    } else {
      console.warn('Invalid thumbnail URL (not absolute):', imageUrl);
      thumbnail = null;
    }
  }

  // Helper function to remove undefined values from objects
  const removeUndefined = (obj) => {
    if (obj === null || obj === undefined) return null;
    if (typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj.map(removeUndefined);

    const cleaned = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = removeUndefined(value);
      }
    }
    return cleaned;
  };

  // Helper to safely convert timestamp to ISO string
  const toISOTimestamp = (ts) => {
    if (!ts) return new Date().toISOString();
    if (typeof ts === 'string') return ts; // Already a string
    if (ts instanceof Date) return ts.toISOString();
    if (ts.toDate) return ts.toDate().toISOString(); // Firestore Timestamp
    return new Date().toISOString();
  };

  const finalData = removeUndefined({
    title,
    thumbnail,
    messages: cleanedMessages.map(msg => ({
      role: msg.role,
      text: msg.text,
      phase: msg.phase,
      options: msg.options || null,
      data: msg.data || null,
      timestamp: toISOTimestamp(msg.timestamp),
      // Persist MINIMAL state snapshot for rewind - full brief is stored at top level
      // Only essential scalar values, no image URLs or large text fields
      stateSnapshot: msg.stateSnapshot ? {
        creativeBrief: createMinimalBriefForSnapshot(msg.stateSnapshot.creativeBrief),
        currentPhase: msg.stateSnapshot.currentPhase || null,
        phaseStep: msg.stateSnapshot.phaseStep ?? 0,
        // Don't store previousOptionsForStep - it can contain image URLs
        // previousOptionsForStep: msg.stateSnapshot.previousOptionsForStep || [],
        // Don't store briefSheetData in snapshots - it's redundant with top-level
        // briefSheetData: msg.stateSnapshot.briefSheetData || null,
        colorPalette: msg.stateSnapshot.colorPalette || [],
        agentHistoryLength: msg.stateSnapshot.agentHistoryLength ?? 0
      } : null
    })),
    creativeBrief: cleanedCreativeBrief,
    briefSheetData: cleanedBriefSheetData,
    generatedImages: validImages,
    creativeMetadata: cleanedCreativeMetadata,
    currentPhase,
    brandName,
    brandId,
    experienceLevel,
    pendingVideoGeneration // Persist pending video generation for resume on refresh
  });

  // Debug: Check for any remaining base64 in the final data
  const jsonString = JSON.stringify(finalData);
  const base64ImageCount = (jsonString.match(/data:image\//g) || []).length;
  const base64VideoCount = (jsonString.match(/data:video\//g) || []).length;
  const base64Count = base64ImageCount + base64VideoCount;
  const sizeInBytes = new Blob([jsonString]).size;

  // Calculate Gemini API costs
  // Pricing constants (from pricingService.js)
  const GEMINI_TEXT_COST_PER_TOKEN = 0.0000006; // $0.60 per 1M tokens
  const GEMINI_IMAGE_COST_PER_IMAGE = 0.039;    // $0.039 per 1024x1024 image
  const MARKUP_MULTIPLIER = 1.10;                // 10% markup
  const CREDITS_PER_DOLLAR = 100000;             // 1 credit = $0.00001

  // Count total tokens from metadata (if tracked in messages)
  let totalTokens = 0;
  finalData.messages.forEach(msg => {
    if (msg.data && msg.data.tokens) {
      totalTokens += msg.data.tokens;
    }
  });

  // Count real generated images (not placeholders)
  const realImageCount = finalData.generatedImages.filter(img => !img.isPlaceholder).length;

  // Calculate costs
  const textCost = totalTokens * GEMINI_TEXT_COST_PER_TOKEN * MARKUP_MULTIPLIER;
  const imageCost = realImageCount * GEMINI_IMAGE_COST_PER_IMAGE * MARKUP_MULTIPLIER;
  const totalCostUSD = textCost + imageCost;
  const totalCredits = Math.ceil(totalCostUSD * CREDITS_PER_DOLLAR);

  console.log('📊 Document Stats:', {
    sizeInBytes,
    sizeInMB: (sizeInBytes / 1024 / 1024).toFixed(2),
    remainingBase64Images: base64ImageCount,
    remainingBase64Videos: base64VideoCount,
    messageCount: finalData.messages.length,
    imageCount: finalData.generatedImages.length,
    realImageCount,
    totalTokens,
    geminiCost: {
      textCostUSD: textCost.toFixed(4),
      imageCostUSD: imageCost.toFixed(4),
      totalCostUSD: totalCostUSD.toFixed(4),
      totalCredits
    }
  });

  if (base64Count > 0) {
    console.warn('⚠️ WARNING: Still found', base64Count, 'base64 media items in final data!');
    // Find where they are
    const findBase64Paths = (obj, path = '') => {
      if (typeof obj === 'string' && (obj.startsWith('data:image/') || obj.startsWith('data:video/'))) {
        const type = obj.startsWith('data:image/') ? 'image' : 'video';
        console.log(`  Found base64 ${type} at:`, path, '- Size:', obj.length);
      } else if (Array.isArray(obj)) {
        obj.forEach((item, i) => findBase64Paths(item, `${path}[${i}]`));
      } else if (obj && typeof obj === 'object') {
        Object.entries(obj).forEach(([key, val]) => findBase64Paths(val, path ? `${path}.${key}` : key));
      }
    };
    findBase64Paths(finalData);
  }

  return finalData;
}

/**
 * Delete a creative chat
 * @param {string} chatId - The chat document ID
 * @returns {Promise<void>}
 */
export async function deleteCreativeChat(chatId) {
  try {
    const chatRef = doc(db, 'creativeChats', chatId);
    await deleteDoc(chatRef);
    console.log('Chat deleted successfully:', chatId);
  } catch (error) {
    console.error('Error deleting creative chat:', error);
    throw error;
  }
}

/**
 * Fork a creative chat from a specific message point
 * Creates a new chat with messages up to the fork point
 * @param {string} originalChatId - The chat to fork from
 * @param {number|null} messageIndex - Index to fork from (null = fork entire chat)
 * @param {string} userId - The user ID
 * @returns {Promise<string>} - The new forked chat ID
 */
export async function forkCreativeChat(originalChatId, messageIndex, userId) {
  try {
    // Load the original chat
    const originalChat = await loadCreativeChat(originalChatId);

    if (!originalChat) {
      throw new Error('Original chat not found');
    }

    // Determine messages to copy (up to and including messageIndex)
    const messagesToCopy = messageIndex !== null
      ? originalChat.messages.slice(0, messageIndex + 1)
      : [...originalChat.messages];

    // Get state snapshot from the fork point message for creativeBrief restoration
    const forkPointMessage = messagesToCopy[messagesToCopy.length - 1];
    const stateAtFork = forkPointMessage?.stateSnapshot || {};

    // Merge the state snapshot brief with the original brief
    // State snapshot has minimal fields, so we need to fill in from original
    const forkedCreativeBrief = {
      ...originalChat.creativeBrief,
      ...(stateAtFork.creativeBrief || {})
    };

    // Calculate fork depth
    const parentForkDepth = originalChat.forkDepth || 0;
    const newForkDepth = parentForkDepth + 1;

    // Determine root chat ID (original ancestor of the fork tree)
    const rootChatId = originalChat.rootChatId || originalChatId;

    // Create the forked chat document
    const forkedChatData = {
      title: `Fork of ${originalChat.title || 'Untitled'}`,
      thumbnail: originalChat.thumbnail,
      messages: messagesToCopy,
      creativeBrief: forkedCreativeBrief,
      briefSheetData: originalChat.briefSheetData || {},
      generatedImages: originalChat.generatedImages || [],
      creativeMetadata: originalChat.creativeMetadata || {},
      currentPhase: stateAtFork.currentPhase || originalChat.currentPhase,
      brandName: originalChat.brandName,
      brandId: originalChat.brandId,
      experienceLevel: originalChat.experienceLevel,
      // Fork metadata
      forkedFromChatId: originalChatId,
      forkedAtMessageIndex: messageIndex,
      forkDepth: newForkDepth,
      rootChatId: rootChatId,
      // Timestamps will be set by saveCreativeChat
      createdAt: serverTimestamp()
    };

    // Save the forked chat
    const newChatId = await saveCreativeChat(forkedChatData, userId);
    console.log('Chat forked successfully:', {
      originalChatId,
      newChatId,
      messageIndex,
      forkDepth: newForkDepth
    });

    return newChatId;
  } catch (error) {
    console.error('Error forking creative chat:', error);
    throw error;
  }
}

/**
 * Build a tree structure from a flat list of chats
 * Groups forked chats under their parent chats
 * @param {Array} chats - Flat array of chats
 * @returns {Array} - Tree structure with children arrays
 */
export function buildChatTree(chats) {
  if (!chats || chats.length === 0) return [];

  const chatMap = new Map();
  const roots = [];

  // First pass: create map of all chats with children arrays
  chats.forEach(chat => {
    chatMap.set(chat.id, { ...chat, children: [] });
  });

  // Second pass: build parent-child relationships
  chats.forEach(chat => {
    const node = chatMap.get(chat.id);

    if (chat.forkedFromChatId && chatMap.has(chat.forkedFromChatId)) {
      // This chat is a fork - add it as a child of its parent
      chatMap.get(chat.forkedFromChatId).children.push(node);
    } else {
      // This is a root chat (either no fork parent or parent was deleted)
      roots.push(node);
    }
  });

  // Sort roots by updatedAt descending (most recent first)
  const sortByUpdatedAt = (a, b) => {
    const aTime = a.updatedAt?.toDate?.() || a.updatedAt || 0;
    const bTime = b.updatedAt?.toDate?.() || b.updatedAt || 0;
    return bTime - aTime;
  };

  // Sort children by createdAt ascending (oldest first, to show fork order)
  const sortChildrenByCreatedAt = (a, b) => {
    const aTime = a.createdAt?.toDate?.() || a.createdAt || 0;
    const bTime = b.createdAt?.toDate?.() || b.createdAt || 0;
    return aTime - bTime;
  };

  // Recursively sort children
  const sortChildren = (node) => {
    if (node.children && node.children.length > 0) {
      node.children.sort(sortChildrenByCreatedAt);
      node.children.forEach(sortChildren);
    }
  };

  roots.sort(sortByUpdatedAt);
  roots.forEach(sortChildren);

  return roots;
}

/**
 * Auto-save creative chat with debouncing
 */
let autoSaveTimeout = null;
export async function scheduleAutoSave(chatState, userId, chatId, onSaveComplete) {
  // Clear existing timeout
  if (autoSaveTimeout) {
    clearTimeout(autoSaveTimeout);
  }

  // Schedule save after 2 seconds of inactivity
  autoSaveTimeout = setTimeout(async () => {
    try {
      // Prepare chat data (uploads images to Storage if needed)
      const chatData = await prepareChatDataForSave(chatState, chatId);

      // Save to Firestore with storage URLs
      const savedChatId = await saveCreativeChat({ ...chatData, id: chatId }, userId);

      if (onSaveComplete) {
        onSaveComplete(savedChatId);
      }
      console.log('Creative chat auto-saved:', savedChatId);
    } catch (error) {
      console.error('Auto-save failed:', error);
    }
  }, 2000);
}

// ============================================================================
// FOLDER CRUD OPERATIONS
// ============================================================================

/**
 * Create a new chat folder
 * @param {Object} folderData - { name, color?, icon? }
 * @param {string} userId - The user ID
 * @param {string} brandId - The brand ID
 * @returns {Promise<string>} - The folder document ID
 */
export async function createChatFolder(folderData, userId, brandId) {
  try {
    const foldersRef = collection(db, FOLDERS_COLLECTION);

    // Get current folder count for order
    const existingFolders = await loadChatFolders(userId, brandId);
    const order = existingFolders.length;

    const folderDoc = {
      name: folderData.name.trim(),
      color: folderData.color || '#6366f1', // Default primary color
      icon: folderData.icon || 'Folder',
      userId,
      brandId,
      order,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(foldersRef, folderDoc);
    return docRef.id;
  } catch (error) {
    console.error('Error creating chat folder:', error);
    throw error;
  }
}

/**
 * Load all chat folders for a user/brand
 * @param {string} userId - The user ID
 * @param {string} brandId - The brand ID (optional)
 * @returns {Promise<Array>} - Array of folders
 */
export async function loadChatFolders(userId, brandId = null) {
  try {
    const foldersRef = collection(db, FOLDERS_COLLECTION);

    let q;
    if (brandId) {
      q = query(
        foldersRef,
        where('userId', '==', userId),
        where('brandId', '==', brandId),
        orderBy('order', 'asc')
      );
    } else {
      q = query(
        foldersRef,
        where('userId', '==', userId),
        orderBy('order', 'asc')
      );
    }

    const querySnapshot = await getDocs(q);
    const folders = [];

    querySnapshot.forEach((doc) => {
      folders.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return folders;
  } catch (error) {
    console.error('Error loading chat folders:', error);
    throw error;
  }
}

/**
 * Update a chat folder
 * @param {string} folderId - The folder document ID
 * @param {Object} updates - { name?, color?, icon?, order? }
 * @param {string} userId - The user ID (for verification)
 * @returns {Promise<void>}
 */
export async function updateChatFolder(folderId, updates, userId) {
  try {
    const folderRef = doc(db, FOLDERS_COLLECTION, folderId);
    const folderDoc = await getDoc(folderRef);

    if (!folderDoc.exists()) {
      throw new Error('Folder not found');
    }

    const folder = folderDoc.data();
    if (folder.userId !== userId) {
      throw new Error('Unauthorized: You do not own this folder');
    }

    await updateDoc(folderRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating chat folder:', error);
    throw error;
  }
}

/**
 * Delete a chat folder (moves chats to root)
 * @param {string} folderId - The folder document ID
 * @param {string} userId - The user ID (for verification)
 * @returns {Promise<void>}
 */
export async function deleteChatFolder(folderId, userId) {
  try {
    const folderRef = doc(db, FOLDERS_COLLECTION, folderId);
    const folderDoc = await getDoc(folderRef);

    if (!folderDoc.exists()) {
      throw new Error('Folder not found');
    }

    const folder = folderDoc.data();
    if (folder.userId !== userId) {
      throw new Error('Unauthorized: You do not own this folder');
    }

    // Move all chats in this folder to root (folderId = null)
    const chatsRef = collection(db, CHATS_COLLECTION);
    const q = query(
      chatsRef,
      where('userId', '==', userId),
      where('folderId', '==', folderId)
    );
    const querySnapshot = await getDocs(q);

    const batch = writeBatch(db);
    querySnapshot.forEach((chatDoc) => {
      batch.update(chatDoc.ref, { folderId: null, updatedAt: serverTimestamp() });
    });

    // Delete the folder
    batch.delete(folderRef);
    await batch.commit();
  } catch (error) {
    console.error('Error deleting chat folder:', error);
    throw error;
  }
}

// ============================================================================
// CHAT ORGANIZATION OPERATIONS (Pin, Archive, Move to Folder)
// ============================================================================

/**
 * Toggle pin status for a chat
 * @param {string} chatId - The chat document ID
 * @param {string} userId - The user ID (for verification)
 * @returns {Promise<boolean>} - New pin status
 */
export async function toggleChatPin(chatId, userId) {
  try {
    const chatRef = doc(db, CHATS_COLLECTION, chatId);
    const chatDoc = await getDoc(chatRef);

    if (!chatDoc.exists()) {
      throw new Error('Chat not found');
    }

    const chat = chatDoc.data();
    if (chat.userId !== userId) {
      throw new Error('Unauthorized: You do not own this chat');
    }

    const newPinStatus = !chat.isPinned;
    await updateDoc(chatRef, {
      isPinned: newPinStatus,
      updatedAt: serverTimestamp()
    });

    return newPinStatus;
  } catch (error) {
    console.error('Error toggling chat pin:', error);
    throw error;
  }
}

/**
 * Toggle archive status for a chat
 * @param {string} chatId - The chat document ID
 * @param {string} userId - The user ID (for verification)
 * @returns {Promise<boolean>} - New archive status
 */
export async function toggleChatArchive(chatId, userId) {
  try {
    const chatRef = doc(db, CHATS_COLLECTION, chatId);
    const chatDoc = await getDoc(chatRef);

    if (!chatDoc.exists()) {
      throw new Error('Chat not found');
    }

    const chat = chatDoc.data();
    if (chat.userId !== userId) {
      throw new Error('Unauthorized: You do not own this chat');
    }

    const newArchiveStatus = !chat.isArchived;
    await updateDoc(chatRef, {
      isArchived: newArchiveStatus,
      updatedAt: serverTimestamp()
    });

    return newArchiveStatus;
  } catch (error) {
    console.error('Error toggling chat archive:', error);
    throw error;
  }
}

/**
 * Move a chat to a folder
 * @param {string} chatId - The chat document ID
 * @param {string|null} folderId - The target folder ID (null for root)
 * @param {string} userId - The user ID (for verification)
 * @returns {Promise<void>}
 */
export async function moveChatToFolder(chatId, folderId, userId) {
  try {
    const chatRef = doc(db, CHATS_COLLECTION, chatId);
    const chatDoc = await getDoc(chatRef);

    if (!chatDoc.exists()) {
      throw new Error('Chat not found');
    }

    const chat = chatDoc.data();
    if (chat.userId !== userId) {
      throw new Error('Unauthorized: You do not own this chat');
    }

    // Verify folder exists if not moving to root
    if (folderId) {
      const folderRef = doc(db, FOLDERS_COLLECTION, folderId);
      const folderDoc = await getDoc(folderRef);
      if (!folderDoc.exists()) {
        throw new Error('Folder not found');
      }
    }

    await updateDoc(chatRef, {
      folderId: folderId || null,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error moving chat to folder:', error);
    throw error;
  }
}

/**
 * Bulk move multiple chats to a folder
 * @param {Array<string>} chatIds - Array of chat document IDs
 * @param {string|null} folderId - The target folder ID (null for root)
 * @param {string} userId - The user ID (for verification)
 * @returns {Promise<number>} - Number of chats moved
 */
export async function bulkMoveChatsToFolder(chatIds, folderId, userId) {
  try {
    // Verify folder exists if not moving to root
    if (folderId) {
      const folderRef = doc(db, FOLDERS_COLLECTION, folderId);
      const folderDoc = await getDoc(folderRef);
      if (!folderDoc.exists()) {
        throw new Error('Folder not found');
      }
    }

    const batch = writeBatch(db);
    let movedCount = 0;

    for (const chatId of chatIds) {
      const chatRef = doc(db, CHATS_COLLECTION, chatId);
      const chatDoc = await getDoc(chatRef);

      if (chatDoc.exists() && chatDoc.data().userId === userId) {
        batch.update(chatRef, {
          folderId: folderId || null,
          updatedAt: serverTimestamp()
        });
        movedCount++;
      }
    }

    await batch.commit();
    return movedCount;
  } catch (error) {
    console.error('Error bulk moving chats:', error);
    throw error;
  }
}

// ============================================================================
// TAG OPERATIONS
// ============================================================================

/**
 * Update tags for a chat
 * @param {string} chatId - The chat document ID
 * @param {Array<string>} tags - The new tags array
 * @param {string} userId - The user ID (for verification)
 * @returns {Promise<void>}
 */
export async function updateChatTags(chatId, tags, userId) {
  try {
    const chatRef = doc(db, CHATS_COLLECTION, chatId);
    const chatDoc = await getDoc(chatRef);

    if (!chatDoc.exists()) {
      throw new Error('Chat not found');
    }

    const chat = chatDoc.data();
    if (chat.userId !== userId) {
      throw new Error('Unauthorized: You do not own this chat');
    }

    // Normalize tags (lowercase, trimmed)
    const normalizedTags = tags
      .map(tag => tag.toLowerCase().trim())
      .filter(tag => tag.length > 0);

    await updateDoc(chatRef, {
      tags: normalizedTags,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating chat tags:', error);
    throw error;
  }
}

/**
 * Add a tag to a chat
 * @param {string} chatId - The chat document ID
 * @param {string} tagName - The tag to add
 * @param {string} userId - The user ID (for verification)
 * @returns {Promise<void>}
 */
export async function addTagToChat(chatId, tagName, userId) {
  try {
    const chatRef = doc(db, CHATS_COLLECTION, chatId);
    const chatDoc = await getDoc(chatRef);

    if (!chatDoc.exists()) {
      throw new Error('Chat not found');
    }

    const chat = chatDoc.data();
    if (chat.userId !== userId) {
      throw new Error('Unauthorized: You do not own this chat');
    }

    const normalizedTag = tagName.toLowerCase().trim();
    const currentTags = chat.tags || [];

    if (!currentTags.includes(normalizedTag)) {
      await updateDoc(chatRef, {
        tags: [...currentTags, normalizedTag],
        updatedAt: serverTimestamp()
      });
    }
  } catch (error) {
    console.error('Error adding tag to chat:', error);
    throw error;
  }
}

/**
 * Remove a tag from a chat
 * @param {string} chatId - The chat document ID
 * @param {string} tagName - The tag to remove
 * @param {string} userId - The user ID (for verification)
 * @returns {Promise<void>}
 */
export async function removeTagFromChat(chatId, tagName, userId) {
  try {
    const chatRef = doc(db, CHATS_COLLECTION, chatId);
    const chatDoc = await getDoc(chatRef);

    if (!chatDoc.exists()) {
      throw new Error('Chat not found');
    }

    const chat = chatDoc.data();
    if (chat.userId !== userId) {
      throw new Error('Unauthorized: You do not own this chat');
    }

    const normalizedTag = tagName.toLowerCase().trim();
    const currentTags = chat.tags || [];

    await updateDoc(chatRef, {
      tags: currentTags.filter(t => t !== normalizedTag),
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error removing tag from chat:', error);
    throw error;
  }
}

/**
 * Get all unique tags for a user's chats (optionally filtered by brand)
 * @param {string} userId - The user ID
 * @param {string} brandId - The brand ID (optional)
 * @returns {Promise<Array<{name: string, count: number}>>} - Array of tags with counts
 */
export async function getChatTags(userId, brandId = null) {
  try {
    const chats = await loadCreativeChats(userId, brandId);

    // Count tag occurrences
    const tagCounts = {};
    chats.forEach(chat => {
      if (chat.tags && Array.isArray(chat.tags)) {
        chat.tags.forEach(tag => {
          tagCounts[tag] = (tagCounts[tag] || 0) + 1;
        });
      }
    });

    // Convert to array and sort by count
    return Object.entries(tagCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  } catch (error) {
    console.error('Error getting chat tags:', error);
    throw error;
  }
}

/**
 * Rename a tag across all chats for a user/brand
 * @param {string} oldTagName - The old tag name
 * @param {string} newTagName - The new tag name
 * @param {string} userId - The user ID
 * @param {string} brandId - The brand ID (optional)
 * @returns {Promise<number>} - Number of chats updated
 */
export async function renameChatTag(oldTagName, newTagName, userId, brandId = null) {
  try {
    const normalizedOld = oldTagName.toLowerCase().trim();
    const normalizedNew = newTagName.toLowerCase().trim();

    if (normalizedOld === normalizedNew) {
      return 0;
    }

    // Get all chats with the old tag
    const chats = await loadCreativeChats(userId, brandId);
    const chatsWithTag = chats.filter(chat =>
      chat.tags && chat.tags.includes(normalizedOld)
    );

    if (chatsWithTag.length === 0) {
      return 0;
    }

    const batch = writeBatch(db);

    chatsWithTag.forEach(chat => {
      const chatRef = doc(db, CHATS_COLLECTION, chat.id);
      const newTags = chat.tags
        .map(t => t === normalizedOld ? normalizedNew : t)
        .filter((t, i, arr) => arr.indexOf(t) === i); // Remove duplicates

      batch.update(chatRef, {
        tags: newTags,
        updatedAt: serverTimestamp()
      });
    });

    await batch.commit();
    return chatsWithTag.length;
  } catch (error) {
    console.error('Error renaming chat tag:', error);
    throw error;
  }
}

/**
 * Delete a tag from all chats for a user/brand
 * @param {string} tagName - The tag to delete
 * @param {string} userId - The user ID
 * @param {string} brandId - The brand ID (optional)
 * @returns {Promise<number>} - Number of chats updated
 */
export async function deleteChatTagFromAll(tagName, userId, brandId = null) {
  try {
    const normalizedTag = tagName.toLowerCase().trim();

    // Get all chats with this tag
    const chats = await loadCreativeChats(userId, brandId);
    const chatsWithTag = chats.filter(chat =>
      chat.tags && chat.tags.includes(normalizedTag)
    );

    if (chatsWithTag.length === 0) {
      return 0;
    }

    const batch = writeBatch(db);

    chatsWithTag.forEach(chat => {
      const chatRef = doc(db, CHATS_COLLECTION, chat.id);
      batch.update(chatRef, {
        tags: chat.tags.filter(t => t !== normalizedTag),
        updatedAt: serverTimestamp()
      });
    });

    await batch.commit();
    return chatsWithTag.length;
  } catch (error) {
    console.error('Error deleting chat tag:', error);
    throw error;
  }
}

/**
 * Bulk add a tag to multiple chats
 * @param {Array<string>} chatIds - Array of chat document IDs
 * @param {string} tagName - The tag to add
 * @param {string} userId - The user ID (for verification)
 * @returns {Promise<number>} - Number of chats updated
 */
export async function bulkTagChats(chatIds, tagName, userId) {
  try {
    const normalizedTag = tagName.toLowerCase().trim();
    const batch = writeBatch(db);
    let taggedCount = 0;

    for (const chatId of chatIds) {
      const chatRef = doc(db, CHATS_COLLECTION, chatId);
      const chatDoc = await getDoc(chatRef);

      if (chatDoc.exists() && chatDoc.data().userId === userId) {
        const currentTags = chatDoc.data().tags || [];
        if (!currentTags.includes(normalizedTag)) {
          batch.update(chatRef, {
            tags: [...currentTags, normalizedTag],
            updatedAt: serverTimestamp()
          });
          taggedCount++;
        }
      }
    }

    await batch.commit();
    return taggedCount;
  } catch (error) {
    console.error('Error bulk tagging chats:', error);
    throw error;
  }
}

/**
 * Bulk archive multiple chats
 * @param {Array<string>} chatIds - Array of chat document IDs
 * @param {string} userId - The user ID (for verification)
 * @returns {Promise<number>} - Number of chats archived
 */
export async function bulkArchiveChats(chatIds, userId) {
  try {
    const batch = writeBatch(db);
    let archivedCount = 0;

    for (const chatId of chatIds) {
      const chatRef = doc(db, CHATS_COLLECTION, chatId);
      const chatDoc = await getDoc(chatRef);

      if (chatDoc.exists() && chatDoc.data().userId === userId) {
        batch.update(chatRef, {
          isArchived: true,
          updatedAt: serverTimestamp()
        });
        archivedCount++;
      }
    }

    await batch.commit();
    return archivedCount;
  } catch (error) {
    console.error('Error bulk archiving chats:', error);
    throw error;
  }
}

// ============================================================================
// ENHANCED SEARCH & FILTER
// ============================================================================

/**
 * Count total forks (children) for a chat recursively
 * @param {Object} chatNode - Chat node with children array
 * @returns {number} - Total fork count including nested forks
 */
export function countTotalForks(chatNode) {
  if (!chatNode.children || chatNode.children.length === 0) {
    return 0;
  }

  let count = chatNode.children.length;
  chatNode.children.forEach(child => {
    count += countTotalForks(child);
  });

  return count;
}

/**
 * Flatten a chat tree back to a flat array (most recent first)
 * @param {Array} tree - Chat tree from buildChatTree
 * @returns {Array} - Flat array of chats sorted by updatedAt
 */
export function flattenChatTree(tree) {
  const result = [];

  const traverse = (nodes) => {
    nodes.forEach(node => {
      const { children, ...chat } = node;
      result.push({ ...chat, childCount: children?.length || 0 });
      if (children && children.length > 0) {
        traverse(children);
      }
    });
  };

  traverse(tree);

  // Sort by updatedAt descending (most recent first)
  result.sort((a, b) => {
    const aTime = a.updatedAt?.toDate?.() || a.updatedAt || 0;
    const bTime = b.updatedAt?.toDate?.() || b.updatedAt || 0;
    return bTime - aTime;
  });

  return result;
}

/**
 * Filter and sort chats with various options
 * @param {Array} chats - Array of chats to filter
 * @param {Object} options - Filter options
 * @returns {Array} - Filtered and sorted chats
 */
export function filterAndSortChats(chats, options = {}) {
  const {
    searchQuery = '',
    folderId = null,        // null = all, 'root' = no folder, string = specific folder
    tags = [],              // Array of tags to filter by (AND logic)
    forkDepthFilter = 'all', // 'all', 'root', 'hasForks', 'forks'
    showArchived = false,
    showPinnedOnly = false,
    sortBy = 'recent'       // 'recent', 'oldest', 'alphabetical', 'forkCount'
  } = options;

  let filtered = [...chats];

  // Filter by archived status
  if (!showArchived) {
    filtered = filtered.filter(chat => !chat.isArchived);
  }

  // Filter by pinned
  if (showPinnedOnly) {
    filtered = filtered.filter(chat => chat.isPinned);
  }

  // Filter by folder
  if (folderId === 'root') {
    filtered = filtered.filter(chat => !chat.folderId);
  } else if (folderId) {
    filtered = filtered.filter(chat => chat.folderId === folderId);
  }

  // Filter by tags (AND logic - must have all selected tags)
  if (tags.length > 0) {
    filtered = filtered.filter(chat => {
      const chatTags = chat.tags || [];
      return tags.every(tag => chatTags.includes(tag.toLowerCase()));
    });
  }

  // Filter by fork depth
  if (forkDepthFilter === 'root') {
    // Only root chats (no parent)
    filtered = filtered.filter(chat => !chat.forkedFromChatId);
  } else if (forkDepthFilter === 'hasForks') {
    // Root chats that have children
    filtered = filtered.filter(chat =>
      !chat.forkedFromChatId && chat.childCount > 0
    );
  } else if (forkDepthFilter === 'forks') {
    // Only forked chats
    filtered = filtered.filter(chat => chat.forkedFromChatId);
  }

  // Filter by search query (title, brandName)
  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase().trim();
    filtered = filtered.filter(chat => {
      const title = (chat.title || '').toLowerCase();
      const brandName = (chat.brandName || '').toLowerCase();
      const product = (chat.creativeBrief?.product || '').toLowerCase();
      return title.includes(query) || brandName.includes(query) || product.includes(query);
    });
  }

  // Sort
  if (sortBy === 'oldest') {
    filtered.sort((a, b) => {
      const aTime = a.createdAt?.toDate?.() || a.createdAt || 0;
      const bTime = b.createdAt?.toDate?.() || b.createdAt || 0;
      return aTime - bTime;
    });
  } else if (sortBy === 'alphabetical') {
    filtered.sort((a, b) => {
      const aTitle = (a.title || '').toLowerCase();
      const bTitle = (b.title || '').toLowerCase();
      return aTitle.localeCompare(bTitle);
    });
  } else if (sortBy === 'forkCount') {
    filtered.sort((a, b) => (b.childCount || 0) - (a.childCount || 0));
  } else {
    // Default: recent (by updatedAt)
    filtered.sort((a, b) => {
      const aTime = a.updatedAt?.toDate?.() || a.updatedAt || 0;
      const bTime = b.updatedAt?.toDate?.() || b.updatedAt || 0;
      return bTime - aTime;
    });
  }

  // Pinned items always at top (within their sort order)
  const pinned = filtered.filter(chat => chat.isPinned);
  const unpinned = filtered.filter(chat => !chat.isPinned);

  return [...pinned, ...unpinned];
}
